import { SectionShell } from "@/components/design-system/SectionShell";
import { BrandMotif } from "@/components/ui/brand-motif";

const code = `<section className="relative overflow-hidden bg-cream py-16">
  <BrandMotif variant="rings" className="absolute -top-40 -right-32" />
  <div className="relative mx-auto max-w-7xl px-6">…</div>
</section>`;

const marks = [
  { variant: "rings", title: "Orbit rings", from: "the logo's rings — offset, with the same broken strokes", use: "hero corners and the teal contact band (tone=\"white\"). Navy-light at 50–60% on cream; white at 30–35% on navy." },
  { variant: "disc", title: "Sky disc", from: "the logo's filled centre", use: "behind a section heading or a photo card, at 16–18% so it reads as a wash, not a shape." },
  { variant: "speckle", title: "Ink speckle", from: "the brush texture in the logo's rings", use: "a corner of text-only sections — founder band, testimonials. Change `seed` for a different scatter." },
  { variant: "sprig", title: "Sprig", from: "the plants in every Sanctuary room", use: "the rooms sections only — Where we meet, Online or in person." },
] as const;

export function MotifsSection() {
  return (
    <SectionShell
      id="motifs"
      title="Brand motifs"
      description={
        <>
          Decorative marks grown from the Sanctuary logo — a sky-blue disc inside hand-drawn, slightly broken
          rings. <code>&lt;BrandMotif&gt;</code> renders one as inline SVG (no asset to load, recolours with the
          theme), always <code>aria-hidden</code> and behind the content: the parent is{" "}
          <code>relative overflow-hidden</code> and its content wrapper is <code>relative</code>.
        </>
      }
      whenToUse={
        <ul>
          <li>One motif per section, two at most on a hero.</li>
          <li>Palette tones only: navy-light, sky, sage, white.</li>
          <li>The same four marks on both brands' sites, so the family resemblance holds.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>Never over a face, never on a form.</li>
          <li>Not as an icon or a bullet — these are backgrounds.</li>
        </ul>
      }
      preview={
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
          {marks.map((m) => (
            <div key={m.variant} className="flex flex-col gap-3">
              <div className="relative h-56 overflow-hidden rounded-2xl bg-cream ring-1 ring-hairline">
                <BrandMotif variant={m.variant} size={300} className="absolute -top-16 -right-16" />
              </div>
              <h4>{m.title}</h4>
              <p className="text-sm text-ink-muted">
                <strong>From:</strong> {m.from}
                <br />
                <strong>Use:</strong> {m.use}
              </p>
            </div>
          ))}
          <div className="relative col-span-full h-48 overflow-hidden rounded-2xl bg-teal">
            <BrandMotif variant="rings" tone="white" size={620} opacity={0.35} className="absolute -top-64 -left-56" />
            <div className="relative flex h-full flex-col items-center justify-center gap-1 text-center text-white">
              <span className="font-display text-2xl font-light">Take the first step</span>
              <span className="text-sm text-white/75">rings in white on the contact band</span>
            </div>
          </div>
        </div>
      }
      code={code}
    />
  );
}
